import { useState, useEffect } from 'react'
import { useDateRange } from '../context/DateRangeContext'
import { useLanguage } from '../context/LanguageContext'

export default function DatePickerPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const { startDate, endDate, setDateRange, daysDiff, comparisonLabel } = useDateRange()
  const { t } = useLanguage()

  // Local pending state - only updates global context on Apply
  const [pendingStartDate, setPendingStartDate] = useState(startDate)
  const [pendingEndDate, setPendingEndDate] = useState(endDate)

  // Sync local state when global state changes (e.g., initial load)
  useEffect(() => {
    setPendingStartDate(startDate)
    setPendingEndDate(endDate)
  }, [startDate, endDate])

  // Calculate pending period info for display
  const calculatePendingDays = () => {
    if (!pendingStartDate || !pendingEndDate) return 0
    const start = new Date(pendingStartDate)
    const end = new Date(pendingEndDate)
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
  }

  // Calculate pending comparison label
  const calculatePendingComparisonLabel = () => {
    if (!pendingStartDate || !pendingEndDate) return ''
    const diff = calculatePendingDays()

    if (diff === 1) {
      return t.datePicker.previousDay
    } else {
      return t.datePicker.previousDays.replace('{n}', String(diff))
    }
  }

  const pendingDays = calculatePendingDays()
  const pendingComparisonLabel = calculatePendingComparisonLabel()

  const applyDateRange = () => {
    setDateRange(pendingStartDate, pendingEndDate)
    setIsOpen(false)
  }

  const setQuickRange = (days: number) => {
    const today = new Date()
    let newStartDate: string
    let newEndDate: string

    if (days === 0) {
      // Today only
      newStartDate = today.toISOString().split('T')[0]
      newEndDate = today.toISOString().split('T')[0]
    } else {
      // Last X days: yesterday going back X days (not including today)
      const end = new Date()
      end.setDate(today.getDate() - 1) // Yesterday
      const start = new Date()
      start.setDate(today.getDate() - days) // X days ago
      newStartDate = start.toISOString().split('T')[0]
      newEndDate = end.toISOString().split('T')[0]
    }

    // Apply immediately for quick select
    setDateRange(newStartDate, newEndDate)
    setIsOpen(false)
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-40 bg-[#FBB300] hover:bg-[#F29100] text-[#1A1A1A] px-6 py-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all font-bold flex items-center gap-3"
      >
        <span className="text-2xl">📅</span>
        <div className="text-left">
          <div className="text-xs opacity-90">{t.datePicker.period}</div>
          <div className="text-sm">{daysDiff} {t.datePicker.days}</div>
        </div>
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">📅 {t.datePicker.title}</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 text-3xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Date Inputs */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t.datePicker.fromDate}
              </label>
              <input
                type="date"
                value={pendingStartDate}
                onChange={(e) => setPendingStartDate(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t.datePicker.toDate}
              </label>
              <input
                type="date"
                value={pendingEndDate}
                onChange={(e) => setPendingEndDate(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Quick Select Buttons */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              {t.datePicker.quickSelect}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setQuickRange(0)}
                className="px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-semibold text-sm"
              >
                {t.datePicker.today}
              </button>
              <button
                onClick={() => setQuickRange(7)}
                className="px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold text-sm"
              >
                {t.datePicker.last7Days}
              </button>
              <button
                onClick={() => setQuickRange(30)}
                className="px-4 py-3 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 transition-colors font-semibold text-sm"
              >
                {t.datePicker.last30Days}
              </button>
              <button
                onClick={() => setQuickRange(90)}
                className="px-4 py-3 bg-amber-500 text-gray-900 rounded-lg hover:bg-amber-600 transition-colors font-semibold text-sm"
              >
                {t.datePicker.last90Days}
              </button>
            </div>
          </div>

          {/* Info Section */}
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-200">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span>📊</span> {t.datePicker.selectedPeriod}
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">{t.datePicker.currentPeriod}</span>
                <span className="font-bold text-gray-800">{daysDiff} {t.datePicker.days}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t.datePicker.newPeriod}</span>
                <span className="font-bold text-green-600">{pendingDays} {t.datePicker.days}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t.datePicker.compareWith}</span>
                <span className="font-bold text-yellow-600">{pendingComparisonLabel || comparisonLabel}</span>
              </div>
              <div className="mt-3 pt-3 border-t border-yellow-200">
                <p className="text-xs text-gray-600">
                  ✓ {t.datePicker.allDashboardsUseThisPeriod}
                </p>
              </div>
            </div>
          </div>

          {/* Apply Button */}
          <button
            onClick={applyDateRange}
            className="w-full mt-6 bg-[#FBB300] hover:bg-[#F29100] text-[#1A1A1A] py-4 rounded-lg hover:shadow-lg transition-all font-bold"
          >
            {t.datePicker.apply}
          </button>
        </div>
      </div>
    </>
  )
}
